import React, { useState, useEffect, Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { profileSlice } from "redux/features/profile";
import { changesSlice } from "redux/features/changes";
import { enumeratedDataValuesSlice } from "redux/features/enumeratedDataValues";
import { Message, Form } from "react-bulma-components";
import IconButton from "util/iconButton";
import { FaEdit, FaPlus, FaCheck, FaTimes } from "react-icons/fa";
import ErrorMessage from "util/errorMessage";

import ChangeAffiliationStartDate from "./changeStartDate";

import {
  useAffiliationChangeInformation,
  useAllAffiliationChangeRecords,
  getStatusSummary,
} from "./common";

import { ShowOriginalStatusRecord } from "./statusDetails";

import { PromptLocation } from "components/shared/location";

const { Field, Control, Radio } = Form;

export function AffiliationSummary() {
  const { id, data, registrationPending } = useSelector(profileSlice.selector);
  const dispatch = useDispatch();
  // We can set simple status information, such as being a member of the
  // general public, directly from the summary widget.

  const [
    currentChangeRecord,
    currentChangeKeys,
  ] = useAffiliationChangeInformation(id, "indefinite-future");

  const setCurrentStatus = (field, value) => {
    dispatch(
      changesSlice.actions.setChanged({
        ...currentChangeKeys,
        field,
        value,
      })
    );
    dispatch(
      changesSlice.actions.setChanged({
        ...currentChangeKeys,
        field: "affiliations",
        value: [],
      })
    );
  };

  const clearCurrentStatus = () => {
    dispatch(changesSlice.actions.deleteChangeRecord(currentChangeKeys));
  };

  const saving = currentChangeRecord ? !!currentChangeRecord.pending : false;
  const saveError = currentChangeRecord ? currentChangeRecord.failed : null;

  const saveCurrentStatus = () => {
    dispatch(changesSlice.asyncActions.save(currentChangeKeys));
  };

  const navigate = useNavigate();
  const [currentStatusRecord, ...pastStatusRecords] = getStatusSummary(
    data
  ).reverse();

  const statusDates = [
    ...pastStatusRecords.map((record) => record.through),
    "indefinite-future",
  ];
  const pastEditsInProgress = Object.keys(
    useAllAffiliationChangeRecords(id, statusDates) || {}
  ).filter((dateSpec) => dateSpec !== "indefinite-future");

  const editingPast = pastEditsInProgress.length > 0;
  const enumeratedDataValues = useSelector(enumeratedDataValuesSlice.selector);
  const occupations = enumeratedDataValues.data.occupations;
  const [generalPublic] = occupations.filter((o) => o.symbolic_id === "OTHER");
  const [provideOptional, setProvideOptional] = useState(false);
  const settingGeneralPublic =
    currentChangeRecord &&
    currentChangeRecord.changes.occupation_id === generalPublic.id;

  const generalPublicLocation = currentChangeRecord
    ? currentChangeRecord.changes.location
    : null;

  const clearChanges = () => {
    clearCurrentStatus();
    setProvideOptional(false);
  };

  const [changingCurrentFrom, setChangingCurrentFrom] = useState(false);

  const missing =
    !currentStatusRecord || currentStatusRecord.statuses.length === 0;
  const required =
    data.require_demographics || registrationPending?.require_demographics;
  useEffect(() => {
    dispatch(profileSlice.actions.setAffiliationIncomplete(missing));
    if (missing && required)
      dispatch(profileSlice.actions.setAffiliationReviewed(false));
  }, [missing, required, dispatch]);

  return (
    <Message {...(missing && required ? { color: "danger" } : {})}>
      <Message.Header>Affiliation/Occupation</Message.Header>
      <Message.Body className="affiliation-status-list summary scrollable">
        <div className="current">
          {pastStatusRecords.length > 0 ? (
            <div className="sectionhead">Current</div>
          ) : null}
          {currentStatusRecord && currentStatusRecord.statuses.length > 0 ? (
            <Fragment>
              <ShowOriginalStatusRecord
                statusRecord={currentStatusRecord}
                isPast={false}
              />
              {changingCurrentFrom ? (
                <ChangeAffiliationStartDate
                  id={id}
                  statusRecord={currentStatusRecord}
                  onFinished={() => setChangingCurrentFrom(false)}
                />
              ) : (
                <div className="buttons">
                  <IconButton
                    icon={FaEdit}
                    onClick={(e) => navigate("affiliation/current")}
                  >
                    {currentChangeRecord
                      ? "Resume Editing"
                      : pastStatusRecords.length > 0
                      ? "Edit Current Affiliation/Occupation"
                      : "Edit"}
                  </IconButton>
                  {!currentChangeRecord && pastStatusRecords.length > 0 ? (
                    <IconButton
                      icon={FaEdit}
                      onClick={(e) => setChangingCurrentFrom(true)}
                    >
                      Change Start{" "}
                      {currentStatusRecord.roundedFrom ? "Month" : "Date"}
                    </IconButton>
                  ) : null}
                </div>
              )}
            </Fragment>
          ) : required ? (
            <div className="missing">
              Missing; please provide this information
              <div className="buttons">
                <IconButton
                  icon={FaPlus}
                  onClick={(e) => navigate("affiliation/current")}
                >
                  {currentChangeRecord
                    ? "Resume Editing"
                    : "Add Affiliation/Occupation Information"}
                </IconButton>
              </div>
            </div>
          ) : (
            <div className="missing-optional">
              <span className="is-italic has-text-weight-bold">Optional</span>{" "}
              (except for scientific workshop/conference/seminar participants)
              <Field>
                <Control>
                  <Radio
                    name="add-optional-information"
                    checked={!!settingGeneralPublic}
                    onChange={(e) => {
                      setCurrentStatus("occupation_id", generalPublic.id);
                      setProvideOptional(false);
                    }}
                  >
                    {" "}
                    {generalPublic.label}
                  </Radio>
                </Control>
                <Control>
                  <Radio
                    name="add-optional-information"
                    checked={provideOptional}
                    onChange={(e) => {
                      clearCurrentStatus();
                      setProvideOptional(true);
                    }}
                  >
                    {" "}
                    Studying or working in a mathematics-related field
                  </Radio>
                </Control>
              </Field>
              {settingGeneralPublic ? (
                <PromptLocation
                  location={generalPublicLocation}
                  setLocation={(loc) => setCurrentStatus("location", loc)}
                />
              ) : null}
              <Field>
                {saveError ? (
                  <ErrorMessage header="Unable to Save Changes">
                    {saveError.message}
                  </ErrorMessage>
                ) : null}
                {saving && !saveError ? (
                  <div className="saving">Saving; please wait</div>
                ) : null}
                <div className="buttons">
                  {provideOptional ? (
                    <IconButton
                      icon={FaEdit}
                      onClick={(e) => navigate("affiliation/current")}
                    >
                      Continue (Provide Details) ...
                    </IconButton>
                  ) : settingGeneralPublic ? (
                    <IconButton icon={FaCheck} onClick={saveCurrentStatus}>
                      Save Changes
                    </IconButton>
                  ) : null}
                  {provideOptional || settingGeneralPublic ? (
                    <IconButton icon={FaTimes} onClick={clearChanges}>
                      Cancel Changes
                    </IconButton>
                  ) : null}
                </div>{" "}
              </Field>
            </div>
          )}
        </div>
        {pastStatusRecords.length > 0 ? (
          <div className="past">
            <div className="sectionhead">Past</div>
            {pastStatusRecords.reverse().map((statusRecord, index) => (
              <ShowOriginalStatusRecord
                statusRecord={statusRecord}
                isPast
                key={index}
              />
            ))}
            {editingPast ? (
              <div className="unsaved-changes-message">
                You have unsaved changes to your past affiliations
              </div>
            ) : null}
            <div className="buttons">
              <IconButton
                icon={FaEdit}
                onClick={(e) =>
                  navigate(
                    editingPast
                      ? "affiliation/past/" + pastEditsInProgress[0]
                      : "affiliation/past"
                  )
                }
              >
                {editingPast
                  ? "Resume Editing"
                  : "Make a Correction to Past Affiliation/Occupation Information"}
              </IconButton>
            </div>
          </div>
        ) : null}
      </Message.Body>
    </Message>
  );
}

export default AffiliationSummary;
